/* @jsx h */
import { defineComponent, h, Ref, ref } from "vue";
import { Payment } from "@lemmonhq/types";
import { formatPriceInLocale, getOrderDisplayName } from "@lemmonhq/orders";

import { TransactionDetails } from "./TransactionDetails";
import { getStatusClass } from "./helpers";

import { TransactionListProps } from "./TransactionList";
import { useShop } from "@app/vue/providers/ShopConnection";
import { useAuth } from "@app/vue/providers/Auth";

export const ResponsiveTransactionItem = defineComponent<{
  payment: Payment;
  selectedPayment: Ref<Payment | null>;
}>({
  name: "TransactionList",
  props: [
    "payment",
    "selectedPayment",
    "transactions",
    "onPrintReceipt",
    "onShowDigitalReceipt",
    "onShowCreateCreditNode",
    "onShowEditPayment",
    "showDetailsInModal",
    "onPaymentShow",
  ],
  setup(props) {
    const auth = useAuth();
    const { payment, selectedPayment } = props;
    const orders = (
      !payment.order && !payment.root_table_orders
        ? []
        : payment.root_table_orders || [payment.order]
    ).filter((el) => el && el.display_id);
    return () => {
      const { tables } = useShop();
      const table = payment.table_id
        ? tables.value.find((el) => el.id === payment.table_id)
        : null;
      return (
        <div
          key={payment.id}
          onClick={() => (selectedPayment.value = payment)}
          class=" cursor-pointer rounded-lg p-3 border  transition-colors"
        >
          <div class="flex items-center gap-4">
            <div class="flex-1">
              <div class="text-sm  flex items-center gap-2">
                <span class="uppercase flex items-center">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    credit_card
                  </span>{" "}
                  {payment.method}
                </span>{" "}
                {orders.length > 0 ? (
                  <span class="flex items-center">
                    <span class="material-symbols-outlined mr-1 text-2xl">
                      order_approve
                    </span>
                    {orders.map((o) => getOrderDisplayName(o)).join(", ")}
                  </span>
                ) : null}
              </div>
              <div class="text-lg flex  flex items-center gap-2">
                <div class="flex items-center justify-center text-accent">
                  <span class="material-symbols-outlined mr-1 text-2xl">
                    payments
                  </span>{" "}
                  {formatPriceInLocale(
                    payment.amount + (payment.tips || 0),
                    true,
                    2,
                    auth.user.value?.restaurant.currency || "CHF",
                  )}{" "}
                </div>
                <div class="text-1xl">
                  {table ? ` | ${table.table.name}` : ""}
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-sm ">
                {new Date(payment.created_at).toLocaleTimeString()}
                {" | "}
                {new Date(payment.created_at).toLocaleDateString()}
              </div>
              <div
                class={`uppercase font-medium ${getStatusClass(
                  payment.status,
                )}`}
              >
                {payment.status}
              </div>
            </div>
          </div>
        </div>
      );
    };
  },
});
export const ResponsiveTransactionList = defineComponent<TransactionListProps>({
  name: "TransactionList",
  props: [
    "payment",
    "selectedPayment",
    "transactions",
    "onPrintReceipt",
    "onShowDigitalReceipt",
    "onShowCreateCreditNode",
    "onShowEditPayment",
    "onShowVoidPayment",
    "showDetailsInModal",
    "onPaymentShow",
  ],
  setup(props) {
    const selectedPayment = ref<Payment | null>(null);

    const renderBottomSheet = () => {
      if (!selectedPayment.value) return null;

      return (
        <TransactionDetails
          payment={selectedPayment.value}
          showDetailsInModal={props.showDetailsInModal}
          onPrintReceipt={props.onPrintReceipt}
          onShowDigitalReceipt={props.onShowDigitalReceipt}
          onShowEditPayment={props.onShowEditPayment}
          onShowVoidPayment={props.onShowVoidPayment}
          onShowCreateCreditNode={props.onShowCreateCreditNode}
          onClose={() => (selectedPayment.value = null)}
        />
      );
    };

    return () => (
      <div class="flex-1 overflow-auto">
        <div class="space-y-2 p-2">
          {props.transactions.map((payment) => {
            return (
              <ResponsiveTransactionItem
                payment={payment}
                selectedPayment={selectedPayment}
              />
            );
          })}
        </div>
        {renderBottomSheet()}
      </div>
    );
  },
});
